import React, { useCallback } from 'react';
import clsx from 'clsx';
import { makeStyles } from 'react-scale/components/design-system/styles';
import ReactTooltip from 'react-tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Kbd from 'react-scale/components/design-system/Kbd';
import { getLabelColor } from '@scale/frontend-lib/annotation';
import {
  isCheckEveryWorkflowStep,
  isAnnotationWorkflowStep,
} from '@scale/shared/taskerWorkflow/TaskerWorkflow';
import DefaultToolTipHeader from './DefaultToolTipHeader';
import { getFriendlyAnnotationId } from '@scale/shared/annotation';
import AnnotationListItemHeaderThumbnail from 'react-scale/components/scalers/AnnotationListItem/AnnotationListItemHeaderThumbnail';
import _ from 'lodash';
import { CheckEveryToolTipHeaderProps } from '../../types';

const useStyles = makeStyles({
  header: {
    minHeight: 25,
    display: 'flex',
    fontWeight: 200,
    fontSize: '16px',
    justifyContent: 'space-between',
    '&.isCompleted': {
      opacity: 0.3,
    },
    padding: 8,
    borderBottom: '1px solid var(--color-RemoGray10)',
  },
  title: {
    display: 'flex',
    flexWrap: 'wrap',
    fontWeight: 500,
  },
  iconToggle: {
    cursor: 'pointer',
    fontSize: '16px',
  },
  inlineIcon: {
    padding: '0 8px',
  },
  annotationIdDisplay: {
    padding: '4px 0px',
    display: 'flex',
    flexWrap: 'wrap',
    fontWeight: 500,
    fontSize: '13px',
  },
});

/**
 * Default ToolTip for GWFBar - Shows Current Step Name & Close Button
 */
const CheckEveryHeader: React.FC<CheckEveryToolTipHeaderProps> = props => {
  const classes = useStyles();

  const { toggleIsGuidedWorkflowTooltipOpen, workflowState } = props.labelerStateHook.workflowHook;
  const { currentStep } = workflowState;

  const isCompleted = workflowState.completedStepIds.includes(currentStep.id);

  const handleCloseTooltip: React.MouseEventHandler = useCallback(
    e => {
      e.stopPropagation();
      toggleIsGuidedWorkflowTooltipOpen();
    },
    [toggleIsGuidedWorkflowTooltipOpen],
  );

  // If this is not a CheckEveryParentNode or AnnotationNode, return a
  if (!isCheckEveryWorkflowStep(currentStep) && !isAnnotationWorkflowStep(currentStep)) {
    return <DefaultToolTipHeader {...props} />;
  }

  const color = getLabelColor(
    currentStep.linkedLabelType,
    props.labelerStateHook.breakdownHook.labelColors,
  );

  const shouldShowAnnotationIdDisplay =
    isAnnotationWorkflowStep(currentStep) &&
    (workflowState.activeCheckEveryAnnotation || workflowState.lockedCheckEveryAnnotation);
  const getAnnotationIdDisplay = () => {
    const annotation = _.find(
      props.labelerStateHook.annotationsHook.annotations,
      annotation => annotation.uuid === currentStep.linkedAnnotationId,
    );

    if (!annotation) {
      return null;
    }

    const thumbnail = (
      <div className={classes.inlineIcon}>
        <AnnotationListItemHeaderThumbnail annotation={annotation} color={color} size={16} />
      </div>
    );

    // Should always exist when on AnnotationWorkflowStep
    const annotationId = currentStep.linkedAnnotationId!;
    return (
      <div className={classes.annotationIdDisplay}>
        Checking:
        {thumbnail}
        {getFriendlyAnnotationId(annotationId)}
      </div>
    );
  };

  return (
    <div className={clsx(classes.header, { isCompleted })}>
      <div>
        <div className={classes.title}>
          {'Check every'}
          <div className={classes.inlineIcon}>
            <FontAwesomeIcon icon="square" color={color} />
          </div>

          {currentStep.linkedLabelType}
        </div>
        {shouldShowAnnotationIdDisplay && getAnnotationIdDisplay()}
      </div>
      <div
        className={classes.iconToggle}
        onClick={handleCloseTooltip}
        data-tip
        data-for="nextButton"
        data-effect="solid"
        data-place="bottom"
      >
        <ReactTooltip id="nextButton">
          {
            <div>
              Open / Close Tooltip <Kbd>T</Kbd>
            </div>
          }
        </ReactTooltip>
        <FontAwesomeIcon icon="times" color="grey" />
      </div>
    </div>
  );
};

export default CheckEveryHeader;
